import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import { Tag, Spin, Empty } from "antd";
import {
  LandsatMosaic_Item,
  remote_LandsatMosaic_GetPage,
} from "~/common/typescript/apis/RemoteImageAlertAPIs";
import LandsatMosaicItem from "./LandsatMosaicItem";
import LandsatMosaicFilterPanel from "./LandsatMosaicFilterPanel";
import { CalendarOutlined, AppstoreOutlined } from "@ant-design/icons";
import { useAtom, useSetAtom } from 'jotai';
import {
  satelliteRecordCountAtom,
  satelliteFilterTriggerAtom,
} from '../../store';
import {
  landsatMosaicYearAtom,
  landsatMosaicPeriodAtom,
} from './store';

const LandsatMosaicFinder: React.FC = () => {
  const [list, setList] = useState<LandsatMosaic_Item[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterVisible, setFilterVisible] = useState(false);

  const [year, setYear] = useAtom(landsatMosaicYearAtom);
  const [period, setPeriod] = useAtom(landsatMosaicPeriodAtom);

  const setRecordCount = useSetAtom(satelliteRecordCountAtom);
  const setFilterTrigger = useSetAtom(satelliteFilterTriggerAtom);

  const fetchData = async (overrideYear?: number, overridePeriod?: number) => {
    setLoading(true);
    try {
      const y = overrideYear !== undefined ? overrideYear : year;
      const p = overridePeriod !== undefined ? overridePeriod : period;
      const data = await remote_LandsatMosaic_GetPage({
        year: y,
        period: p,
      });
      setList(data || []);
      setFilterVisible(false);
    } catch (err) {
      console.error('Failed to load Landsat Mosaic list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update record count when the list changes
  useEffect(() => {
    setRecordCount(list.length);
  }, [list, setRecordCount]);

  // Register the filter trigger function for top bar filter icon
  useEffect(() => {
    setFilterTrigger(() => {
      setFilterVisible(true);
    });
    return () => {
      setFilterTrigger(null);
    };
  }, [setFilterTrigger]);

  const handleResetFilter = () => {
    setYear(undefined);
    setPeriod(undefined);
    fetchData(undefined, undefined);
  };

  return (
    <>
      <UI_Wrapper>
        {/* Active filters display */}
        {(year !== undefined || period !== undefined) && (
          <div style={{ marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {year !== undefined && (
              <Tag
                color="blue"
                icon={<CalendarOutlined />}
                closable
                onClose={() => {
                  setYear(undefined);
                  fetchData(undefined, period);
                }}
              >
                Năm: {year}
              </Tag>
            )}
            {period !== undefined && (
              <Tag
                color="orange"
                icon={<AppstoreOutlined />}
                closable
                onClose={() => {
                  setPeriod(undefined);
                  fetchData(year, undefined);
                }}
              >
                Kỳ: {period} (2 tháng)
              </Tag>
            )}
          </div>
        )}

        {/* Content list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <UI_LoadingContainer>
              <Spin size="large" tip="Đang tải danh mục ảnh Landsat ghép định kỳ..." />
            </UI_LoadingContainer>
          ) : list.length === 0 ? (
            <UI_EmptyContainer>
              <Empty description="Không tìm thấy ảnh Landsat ghép nào" />
            </UI_EmptyContainer>
          ) : (
            <UI_List_Wrapper>
              {list.map((item) => (
                <LandsatMosaicItem key={item.id} item={item} />
              ))}
            </UI_List_Wrapper>
          )}
        </div>
      </UI_Wrapper>

      {/* Filter drawer */}
      <LandsatMosaicFilterPanel
        visible={filterVisible}
        onClose={() => setFilterVisible(false)}
        year={year}
        setYear={setYear}
        period={period}
        setPeriod={setPeriod}
        onSearch={() => fetchData()}
        onReset={handleResetFilter}
      />
    </>
  );
};

export default memo(LandsatMosaicFinder);

const UI_Wrapper = styled.div`
  padding: 8px;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const UI_List_Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const UI_LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 240px;
`;

const UI_EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 240px;
`;
